import type {
  DispatchAssignedRetrieveApiArg,
  DispatchRetrieveApiArg,
  DispatchUnassignedRetrieveApiArg,
} from '@bambi/api-client/schemas';
import { createSelector } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { makeDispatchTimeRange } from './makeDispatchTimeRange';
import type { RootState } from 'src/app/store/types';

export const formatDateTime = (time: string, selectedDate: string) => {
  const timeSplit = time.split(':');
  const hour = parseInt(timeSplit[0]);
  const minute = parseInt(timeSplit[1]);
  return DateTime.fromISO(selectedDate).set({ hour, minute }).toUTC().toISO();
};

/**
 * The dispatch board's query args, derived from dispatcher slice state.
 * Extracted from useDispatchRepository so that non-hook code (selectors,
 * thunks, test seeding) can compute the exact cache key the board query
 * uses. Memoized so repeated reads return the same args object.
 */
export const selectDispatchQueryArgs = createSelector(
  [
    (state: RootState) =>
      state.dispatcher.selectedDate || DateTime.now().toISODate(),
    (state: RootState) => state.dispatcher.dispatchCalendarFilters,
    (state: RootState) => state.dispatcher.unassignedTripsFilters,
    (state: RootState) => state.dispatcher.unassignedTripsSorting,
    (state: RootState) => state.dispatcher.unassignedTripsSortingDirection,
  ],
  (
    selectedDate,
    dispatchCalendarFilters,
    unassignedTripsFilters,
    unassignedTripsSorting,
    unassignedTripsSortingDirection
  ): DispatchRetrieveApiArg &
    Required<Pick<DispatchRetrieveApiArg, 'rangeStart' | 'rangeEnd'>> => {
    const dispatchTimeRange = makeDispatchTimeRange(selectedDate);

    return {
      assignedTripStatus: dispatchCalendarFilters.statuses.join(','),
      rangeStart: dispatchTimeRange.start,
      rangeEnd: dispatchTimeRange.end,
      unassignedRangeStart: unassignedTripsFilters.startTime
        ? formatDateTime(unassignedTripsFilters.startTime, selectedDate)
        : undefined,
      unassignedRangeEnd: unassignedTripsFilters.endTime
        ? formatDateTime(unassignedTripsFilters.endTime, selectedDate)
        : undefined,
      unassignedPayers: unassignedTripsFilters.payers
        .map((payer) => payer.id)
        .join(','),
      unassignedSpaceTypes: unassignedTripsFilters.spaceTypes.join(','),
      unassignedServiceTypes: unassignedTripsFilters.serviceTypes.join(','),
      zipcodeSearch: unassignedTripsFilters.zipCodes.join(','),
      countySearch: unassignedTripsFilters.counties
        .map((county) => `${county.county}:${county.state}`)
        .join(','),
      driverTagSearch: dispatchCalendarFilters.driverTags
        .map((tag) => tag.tag_name)
        .join(','),
      vehicleTagSearch: dispatchCalendarFilters.vehicleTags
        .map((tag) => tag.tag_name)
        .join(','),
      // TODO: should probably rename this to unassignedTripTagSearch
      // to stay consistent with other unassigned trip filters
      tripTagSearch: unassignedTripsFilters.tags
        .map((tag) => tag.tag_name)
        .join(','),
      passengerTagSearch: unassignedTripsFilters.passengerTags
        .map((tag) => tag.tag_name)
        .join(','),
      unassignedPriceMin: unassignedTripsFilters.price?.min,
      unassignedPriceMax: unassignedTripsFilters.price?.max,
      unassignedOrdering: `${
        unassignedTripsSortingDirection === 'asc' ? '' : '-'
      }${unassignedTripsSorting}`,
    };
  }
);

export const selectDispatchAssignedQueryArgs = createSelector(
  [selectDispatchQueryArgs],
  (args): DispatchAssignedRetrieveApiArg => ({
    rangeStart: args.rangeStart,
    rangeEnd: args.rangeEnd,
    assignedTripStatus: args.assignedTripStatus,
    driverTagSearch: args.driverTagSearch,
    vehicleTagSearch: args.vehicleTagSearch,
  })
);

export const selectDispatchUnassignedQueryArgs = createSelector(
  [selectDispatchQueryArgs],
  ({
    assignedTripStatus: _assignedTripStatus,
    driverTagSearch: _driverTagSearch,
    vehicleTagSearch: _vehicleTagSearch,
    ...args
  }): DispatchUnassignedRetrieveApiArg => args
);
